import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { LanguageUtils, PlusButton } from '@pbt/pbt-ui-components'

import type {
  CreateInventoryOrderItemReceiptInput,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import { getInventoryOrdersIsUpdating } from '~/store/duck/inventoryOrders'
import { getProductSizeUnit } from '~/store/reducers/constants'

import {
  extractReceiptInputFromFields,
  type ReceiptFieldGroup,
  type ReceiptFields,
} from './hooks/useReceiptFields'
import { OrderItemReceipt } from './OrderItemReceipt'

interface OrderItemReceiptsListProps {
  addDraftReceipt: () => void
  deleteDraftReceipt: (id: string) => void
  draftReceiptFields: ReceiptFieldGroup[]
  duplicateDraftReceipt: (values: CreateInventoryOrderItemReceiptInput) => void
  inventoryOrderItem: InventoryOrderItem
  isViewOnly?: boolean
  receiptFields: ReceiptFieldGroup[]
}

export const OrderItemReceiptsList = ({
  addDraftReceipt,
  duplicateDraftReceipt,
  deleteDraftReceipt,
  draftReceiptFields,
  inventoryOrderItem,
  isViewOnly = false,
  receiptFields,
}: OrderItemReceiptsListProps) => {
  const { t } = useTranslation('Common')
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const productSizeUnit = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageUnits.id,
    ProductSizeUnit,
  )

  const handlePlusButtonClick = () => {
    if (isUpdating) {
      return
    }

    addDraftReceipt()
  }

  const handleDuplicate = (fields: ReceiptFields) => {
    if (isUpdating) {
      return
    }

    duplicateDraftReceipt(extractReceiptInputFromFields(fields))
  }

  const renderReceipts = (receipts: ReceiptFieldGroup[], isDraft?: boolean) =>
    receipts.map(({ id, fields }, index) => {
      const canDelete =
        !isViewOnly &&
        !isUpdating &&
        isDraft &&
        (receiptFields.length > 0 || index > 0)
      const canDuplicate = !isViewOnly && !isUpdating
      return (
        <OrderItemReceipt
          canDelete={canDelete}
          canDuplicate={canDuplicate}
          fields={fields}
          isDraft={isDraft}
          isViewOnly={isViewOnly}
          key={id}
          orderedQuantity={inventoryOrderItem.orderedQuantity}
          perPackageAmount={inventoryOrderItem.perPackageAmount}
          productSizeUnit={productSizeUnit}
          onDelete={() => deleteDraftReceipt(id)}
          onDuplicate={() => handleDuplicate(fields)}
        />
      )
    })

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {renderReceipts(receiptFields)}
      {renderReceipts(draftReceiptFields, true)}
      {!isViewOnly && (
        <PlusButton
          label={t('Common:ADD_ROW')}
          sx={{ width: 'fit-content' }}
          onClick={handlePlusButtonClick}
        />
      )}
    </Box>
  )
}
